import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Api } from '../api'
import {
  ApiError,
  type AdminReport,
  type ApiFailure,
  type ColumnDefinition,
  type ColumnDefinitionRow,
  type ColumnGroupPage,
  type ColumnGroupRow,
  type ColumnSettingEffect,
} from '../api/types'
import { Badge, Button, Card, EmptyState, Loading, Notice, PageTabs, SearchInput, SearchSelect, inputClass } from '../components/ui'
import { DataGrid, type Column } from '../components/DataGrid'
import { FailureState } from '../components/FailureState'
import { SlideOver } from '../components/SlideOver'
import { CheckTile, FilterSlot, InfoStrip, Verdict, messageOf } from './accessWords'

/**
 * COLUMN DEFINITION UNDER THE NEW MODEL (060; D53, owner 28 Oct 2026).
 *
 * The managers asked for "column groups" in place of the fixed figures: an admin
 * makes a group for ONE report, names and describes it, and then says, column by
 * column, who sees it:
 *
 *   No one          nobody but the system roles (every column starts here)
 *   Everyone        anyone who may open the report
 *   Column groups   anyone holding any one of the groups ticked, all of this report
 *
 * "No one" was "Not configured" until the owner renamed it, everywhere on the page,
 * and put it first (29 Oct 2026).
 *
 * The page counts them in the owner's three words (28 Oct 2026): Default columns
 * (the ones the report needs, a grouping dimension, always shown), Changeable
 * columns (set to Everyone or to column groups) and No one; a dropdown
 * shows one kind at a time. Who holds a
 * group is set on HR roles, a person's HR main scope and their MBI additional
 * scopes, each offering only the groups of the reports it gives.
 *
 * Two tabs, one report at a time: Columns (who sees each) and Column groups (make,
 * rename, describe, delete when nothing uses it). Every change says, in people,
 * what it will do before it is saved, and needs a reason. The database is the
 * control (51600, 51627-51636); this screen states the rules early.
 */
export function ColumnDefinitionV2({
  api,
  reports,
  reportSlug,
  onPickReport,
}: {
  api: Api
  /** The registry, fetched once by the page. Null while it is still loading. */
  reports: AdminReport[] | null
  reportSlug: string
  onPickReport: (slug: string) => void
}) {
  const [tab, setTab] = useState<'columns' | 'groups'>('columns')
  const [data, setData] = useState<ColumnDefinition | null>(null)
  const [loadFailed, setLoadFailed] = useState<ApiFailure | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<ColumnDefinitionRow | null>(null)
  const [groupPanel, setGroupPanel] = useState<{ group: ColumnGroupRow | null } | null>(null)
  const [deleting, setDeleting] = useState<ColumnGroupRow | null>(null)

  const report = reports?.find((r) => r.slug === reportSlug)
  const reportName = report?.displayName ?? 'this report'

  // The last report asked for: a slow answer for the one before must not land on it (review of 18c).
  const asked = useRef<string | null>(null)
  const load = useCallback(() => {
    if (!reportSlug) return
    asked.current = reportSlug
    setData(null)
    setLoadFailed(null)
    api
      .columnDefinition(reportSlug)
      .then((d) => { if (asked.current === reportSlug) setData(d) })
      .catch((e) => {
        if (asked.current !== reportSlug) return
        setLoadFailed(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not read the column definition.' })
        setData({ columns: [], groups: [], columnCount: null, hiddenCount: null, groupCount: null, outcome: null })
      })
  }, [api, reportSlug])

  // A notice from another report would be read as this one's.
  useEffect(() => { setDone(null); setError(null); load() }, [load])

  const groups = useMemo(() => data?.groups ?? [], [data])
  const finished = (m: string) => { setEditing(null); setGroupPanel(null); setDeleting(null); setError(null); setDone(m); load() }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {(error || done) && (
        <div className="shrink-0 space-y-2">
          {error && <Notice tone="bad" onDismiss={() => setError(null)}>{error}</Notice>}
          {done && <Notice tone="good" onDismiss={() => setDone(null)}>{done}</Notice>}
        </div>
      )}

      <Card className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 flex-wrap items-end gap-3 border-b border-(--color-line) px-5 py-(--band-y)">
          <FilterSlot label="Report" wide>
            <SearchSelect
              label="Report"
              value={reportSlug}
              onChange={(v) => onPickReport(v)}
              disabled={!reports || reports.length === 0}
              placeholder={reports === null ? 'Loading…' : 'Choose a report'}
              options={(reports ?? []).map((r) => ({ value: r.slug, label: r.displayName }))}
            />
          </FilterSlot>
          {tab === 'groups' && data && !loadFailed && (
            <span className="ml-auto pb-1">
              <Button variant="primary" onClick={() => setGroupPanel({ group: null })}>New column group</Button>
            </span>
          )}
        </div>
        <div className="px-5">
          <PageTabs
            label="Column definition"
            value={tab}
            onChange={setTab}
            tabs={[
              { id: 'columns', label: 'Columns', title: `Who sees each column of ${reportName}` },
              { id: 'groups', label: `Column groups · ${data && !loadFailed ? groups.length : '—'}`, title: `The column groups of ${reportName}` },
            ]}
          />
        </div>

        {data === null ? (
          <Loading label="Reading the column definition…" />
        ) : loadFailed ? (
          <FailureState failure={loadFailed} onRetry={load} />
        ) : tab === 'columns' ? (
          <ColumnsView data={data} reportName={reportName} editing={editing} onEdit={setEditing} />
        ) : (
          <GroupsView groups={groups} reportName={reportName} onEdit={(g) => setGroupPanel({ group: g })} onDelete={setDeleting}
            onNew={() => setGroupPanel({ group: null })} />
        )}
      </Card>

      {editing && (
        <ColumnPanel api={api} column={editing} groups={groups} reportName={reportName}
          onClose={() => setEditing(null)} onDone={finished} onError={(m) => { setEditing(null); setError(m) }}
          onMakeGroup={() => { setEditing(null); setTab('groups'); setGroupPanel({ group: null }) }} />
      )}
      {groupPanel && (
        <GroupPanel api={api} group={groupPanel.group} reportSlug={reportSlug} reportName={reportName}
          onClose={() => setGroupPanel(null)} onDone={finished} />
      )}
      {deleting && (
        <DeleteGroupPanel api={api} group={deleting} reportName={reportName} onClose={() => setDeleting(null)} onDone={finished} />
      )}
    </div>
  )
}

type ColumnState = 'REQUIRED' | 'EVERYONE' | 'GROUPS' | 'HIDDEN'
type ColumnKind = 'DEFAULT' | 'CHANGEABLE' | 'NOT_CONFIGURED'

/** The owner's three kinds (28 Oct 2026): a changeable column is one an admin has set, to Everyone or to groups. */
const KINDS: { kind: ColumnKind; name: string; what: string; states: ColumnState[] }[] = [
  { kind: 'DEFAULT', name: 'Default columns', what: 'Columns the report needs. Everyone sees them, and they cannot be changed.', states: ['REQUIRED'] },
  { kind: 'CHANGEABLE', name: 'Changeable columns', what: 'Set to everyone, or to the column groups chosen for them.', states: ['EVERYONE', 'GROUPS'] },
  { kind: 'NOT_CONFIGURED', name: 'No one', what: 'Only the system roles see them. Every column starts here.', states: ['HIDDEN'] },
]
const kindOf = (s: ColumnState) => KINDS.find((k) => k.states.includes(s))!
/** Each kind's dot in the counts: Default grey as the grid's "Default column", No one red as its badge, Changeable navy. */
const KIND_DOT: Record<ColumnKind, string> = {
  DEFAULT: 'bg-(--color-ink-3)',
  CHANGEABLE: 'bg-(--color-navy-700)',
  NOT_CONFIGURED: 'bg-(--color-overdue)',
}
/** Who sees a column, in words: for sorting the grid as it reads. */
const whoWords = (c: ColumnDefinitionRow) =>
  c.visibility === 'REQUIRED' ? 'Default column' : c.visibility === 'EVERYONE' ? 'Everyone'
    : c.visibility === 'HIDDEN' ? 'No one' : `Column groups: ${c.groupNames ?? ''}`
/** A column's group IDs, from the "12, 15" the procedure sends: names are looked up by ID, since a name may hold a comma. */
const idsOf = (c: ColumnDefinitionRow) => (c.groupIds ?? '').split(',').map((x) => Number(x.trim())).filter((n) => n > 0)

function ColumnsView({ data, reportName, editing, onEdit }: {
  data: ColumnDefinition
  reportName: string
  editing: ColumnDefinitionRow | null
  onEdit: (c: ColumnDefinitionRow) => void
}) {
  const [search, setSearch] = useState('')
  // Which kind to show, from the dropdown (owner, 28 Oct 2026); '' is every column.
  const [only, setOnly] = useState<ColumnKind | ''>('')
  const columns = data.columns
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    return columns.filter((c) => (!only || kindOf(c.visibility).kind === only)
      && (!q || [c.displayName, c.columnName, c.groupNames ?? ''].join(' ').toLowerCase().includes(q)))
  }, [columns, search, only])
  const count = (k: ColumnKind) => columns.filter((c) => kindOf(c.visibility).kind === k).length
  const onlyName = KINDS.find((k) => k.kind === only)?.name

  const grid: Column<ColumnDefinitionRow>[] = [
    {
      key: 'displayName', header: 'Column', align: 'left', stickyLeft: true, sortValue: (c) => c.displayName,
      render: (c) => (
        <span className="flex min-w-0 flex-col items-start text-left">
          <span className="font-medium text-(--color-ink)">{c.displayName}</span>
          <span className="max-w-full truncate font-mono text-(length:--text-micro) text-(--color-ink-3)" title={c.columnName}>{c.columnName}</span>
        </span>
      ),
    },
    {
      key: 'who', header: 'Who sees it', align: 'left',
      sortValue: whoWords,
      render: (c) =>
        c.visibility === 'REQUIRED' ? <span className="text-(--color-ink-3)" title="A column the report needs: always shown, and it cannot be changed.">Default column</span>
          : c.visibility === 'EVERYONE' ? <span className="font-medium text-(--color-ink)">Everyone</span>
            : c.visibility === 'HIDDEN' ? <Badge tone="bad">No one</Badge>
              : (
                <span className="inline-flex flex-wrap gap-1">
                  {idsOf(c).map((id) => <GroupChip key={id} name={data.groups.find((g) => g.groupId === id)?.groupName ?? `#${id}`} />)}
                </span>
              ),
    },
    {
      key: 'people', header: 'Users who see it', align: 'right',
      sortValue: (c) => (c.visibility === 'REQUIRED' ? Number.MAX_SAFE_INTEGER : c.peopleSeeing),
      render: (c) =>
        c.visibility === 'REQUIRED' ? <span className="text-(--color-ink-3)">Everyone</span>
          : c.visibility === 'HIDDEN' ? <span className="text-(--color-ink-4)">System roles only</span>
            : <span className="font-medium tabular-nums text-(--color-ink)">{c.peopleSeeing.toLocaleString('en-GB')}</span>,
    },
    {
      key: 'edit', header: ' ', align: 'right', stickyRight: true,
      render: (c) => (
        <button
          type="button"
          disabled={c.isRequired}
          title={c.isRequired ? 'A default column: the report needs it, so it is always shown and cannot be changed.' : `Change who sees ${c.displayName}`}
          onClick={(e) => { e.stopPropagation(); onEdit(c) }}
          className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-1 text-(length:--text-small) font-medium text-(--color-navy-700) transition-colors hover:border-(--color-navy-300) hover:bg-(--color-navy-50) disabled:cursor-not-allowed disabled:border-(--color-line) disabled:text-(--color-ink-4) disabled:hover:bg-(--color-surface)"
        >
          Change…
        </button>
      ),
    },
  ]

  return (
    <>
      <div className="flex shrink-0 flex-col gap-3 border-b border-(--color-line) px-5 py-(--band-y)">
        <div className="flex flex-wrap items-end gap-3">
          <FilterSlot label="Show" wide>
            <SearchSelect label="Show" value={only} onChange={(v) => setOnly(v as ColumnKind | '')}
              options={[{ value: '', label: 'All columns' }, ...KINDS.map((k) => ({ value: k.kind, label: `${k.name} · ${count(k.kind)}` }))]} />
          </FilterSlot>
          <div className="flex min-w-[240px] flex-1 flex-col gap-1 @6xl:max-w-[360px]">
            <span className="eyebrow">Find a column</span>
            <SearchInput label="Search columns" placeholder="Column or column group…" value={search} onChange={setSearch} />
          </div>
          {/* Counts only, in one line beside the filters: the dropdown does the filtering
              (owner, 28 Oct 2026), and the three cards took too much room (30 Oct). */}
          <div className="flex flex-col gap-1 @6xl:ml-auto">
            <span className="eyebrow">Columns on {reportName}</span>
            {/* As tall as the controls beside it (inputClass: 35 px); stacked where a row would wrap. */}
            <dl className="flex min-h-[35px] flex-col overflow-hidden rounded-md border border-(--color-line) bg-(--color-surface) text-(length:--text-small) @3xl:flex-row @3xl:items-stretch">
              {KINDS.map((k) => (
                <div key={k.kind} title={k.what}
                  className="flex items-center justify-between gap-2 border-(--color-line) px-3 py-1 not-first:border-t @3xl:not-first:border-t-0 @3xl:not-first:border-l">
                  <dt className="flex items-center gap-2 text-(--color-ink-2)">
                    <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${KIND_DOT[k.kind]}`} />
                    {k.name}
                  </dt>
                  <dd className="font-semibold tabular-nums text-(--color-ink)">{count(k.kind)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
      {columns.length > 0 && (
        <div className="flex shrink-0 flex-wrap items-center gap-x-3 border-b border-(--color-line) bg-(--color-surface-2) px-5 py-1.5 text-(length:--text-small) text-(--color-ink-3)">
          <span>Showing {visible.length} of {columns.length} {columns.length === 1 ? 'column' : 'columns'} on {reportName}{onlyName ? ` · ${only === 'NOT_CONFIGURED' ? 'set to No one' : `${onlyName.toLowerCase()} only`}` : ''}.</span>
          {(only || search) && (
            <button type="button" onClick={() => { setOnly(''); setSearch('') }} className="font-medium text-(--color-navy-700) hover:underline">Show every column</button>
          )}
        </div>
      )}
      {columns.length === 0 ? (
        <EmptyState title={`No columns are registered for ${reportName}.`} detail="Columns are registered by the report's own deployment script." />
      ) : visible.length === 0 ? (
        <EmptyState title="No column matches." detail="Clear the search, or show all columns." />
      ) : (
        <DataGrid fill columns={grid} rows={visible} isRowActive={(c) => c.reportColumnId === editing?.reportColumnId} />
      )}
      <InfoStrip>
        <strong className="font-semibold text-(--color-ink-2)">Default columns are always shown. Change… sets any other column to No one, Everyone, or column groups of this report.</strong>{' '}
        A user sees a column set to groups when they hold any one of them; who holds a group is set on HR Roles, a
        user&apos;s HR main scope and their MBI additional scopes. A column set to No one, as every column starts, is
        seen only by the system roles, as they see every column.
      </InfoStrip>
    </>
  )
}

function GroupsView({ groups, reportName, onEdit, onDelete, onNew }: {
  groups: ColumnGroupRow[]
  reportName: string
  onEdit: (g: ColumnGroupRow) => void
  onDelete: (g: ColumnGroupRow) => void
  onNew: () => void
}) {
  const used = (g: ColumnGroupRow) => g.columnsN + g.rolesN + g.personSettingsN + g.extraScopesN > 0
  const grid: Column<ColumnGroupRow>[] = [
    {
      key: 'name', header: 'Column group', align: 'left', stickyLeft: true, sortValue: (g) => g.groupName,
      render: (g) => (
        <span className="flex min-w-0 flex-col items-start text-left">
          <span className="font-medium text-(--color-ink)">{g.groupName}</span>
          {g.description && <span className="text-(length:--text-small) text-(--color-ink-3)">{g.description}</span>}
        </span>
      ),
    },
    {
      key: 'columns', header: 'Columns it shows', align: 'left', sortValue: (g) => g.columnsN,
      render: (g) => (g.columnNames ? <span className="text-(--color-ink-2)">{g.columnNames}</span> : <span className="text-(--color-ink-4)">None yet</span>),
    },
    {
      key: 'usedBy', header: 'Given by', align: 'left', sortValue: (g) => g.rolesN + g.personSettingsN + g.extraScopesN,
      render: (g) => {
        const parts = [
          g.rolesN ? `${g.rolesN} HR ${g.rolesN === 1 ? 'role' : 'roles'}` : null,
          g.personSettingsN ? `${g.personSettingsN} ${g.personSettingsN === 1 ? 'override' : 'overrides'}` : null,
          g.extraScopesN ? `${g.extraScopesN} additional ${g.extraScopesN === 1 ? 'scope' : 'scopes'}` : null,
        ].filter(Boolean)
        return parts.length ? <span className="text-(--color-ink-2)">{parts.join(' · ')}</span> : <span className="text-(--color-ink-4)">Nobody yet</span>
      },
    },
    {
      key: 'people', header: 'Users who hold it', align: 'right', sortValue: (g) => g.peopleHolding,
      render: (g) => <span className="font-medium tabular-nums text-(--color-ink)">{g.peopleHolding.toLocaleString('en-GB')}</span>,
    },
    {
      key: 'actions', header: ' ', align: 'right', stickyRight: true,
      render: (g) => (
        <span className="inline-flex gap-1.5">
          <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(g) }}
            className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-1 text-(length:--text-small) font-medium text-(--color-navy-700) hover:border-(--color-navy-300) hover:bg-(--color-navy-50)">
            Edit…
          </button>
          <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(g) }}
            title={used(g) ? 'Still in use: see where, and take it off those first' : `Delete ${g.groupName}`}
            className="rounded-md border border-(--color-line-strong) bg-(--color-surface) px-2.5 py-1 text-(length:--text-small) font-medium text-(--color-overdue) hover:border-(--color-overdue)/50 hover:bg-(--color-overdue)/5">
            {used(g) ? 'Where used…' : 'Delete…'}
          </button>
        </span>
      ),
    },
  ]
  return (
    <>
      {groups.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 px-5 py-10">
          <EmptyState title={`${reportName} has no column groups yet.`}
            detail="Make one, then choose it on the columns it should show, and give it to HR Roles or users." />
          <Button variant="primary" onClick={onNew}>New column group</Button>
        </div>
      ) : (
        <DataGrid fill columns={grid} rows={groups} />
      )}
      <InfoStrip>
        <strong className="font-semibold text-(--color-ink-2)">A column group belongs to {reportName} alone.</strong>{' '}
        It can be chosen only on this report&apos;s columns, and given only where this report is given. Groups add up: someone
        holding two groups sees the columns of both. A group can be deleted once nothing uses it.
      </InfoStrip>
    </>
  )
}

function GroupChip({ name }: { name: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-(--color-line-strong) bg-(--color-surface-2) px-2 py-px text-(length:--text-small) text-(--color-ink)">
      {name}
    </span>
  )
}

/** Who sees one column: no one, everyone, or this report's groups; people before and after, then a reason. */
function ColumnPanel({ api, column, groups, reportName, onClose, onDone, onError, onMakeGroup }: {
  api: Api
  column: ColumnDefinitionRow
  groups: ColumnGroupRow[]
  reportName: string
  onClose: () => void
  onDone: (m: string) => void
  onError: (m: string) => void
  onMakeGroup: () => void
}) {
  const initialVis = column.visibility === 'REQUIRED' ? 'EVERYONE' : column.visibility
  const initialGroups = useMemo(() => idsOf(column), [column])
  const [vis, setVis] = useState<'EVERYONE' | 'HIDDEN' | 'GROUPS'>(initialVis)
  const [picked, setPicked] = useState<number[]>(initialGroups)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<ColumnSettingEffect['summary']>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const groupIds = vis === 'GROUPS' ? picked : []
  const changed = vis !== initialVis || (vis === 'GROUPS' && [...picked].sort().join() !== [...initialGroups].sort().join())
  const valid = vis !== 'GROUPS' || picked.length > 0
  const body = useMemo(() => ({ visibility: vis, groupIds, expectedVersion: column.version }), [vis, groupIds.join(), column.version]) // eslint-disable-line react-hooks/exhaustive-deps

  // THE EFFECT, IN PEOPLE, BEFORE THE PRESS: the same procedure, nothing written.
  useEffect(() => {
    setPreview(null)
    setPreviewError(null)
    if (!changed || !valid) return
    let cancelled = false
    api.columnSettingPreview(column.reportColumnId, body)
      .then((r) => { if (!cancelled) setPreview(r.summary) })
      .catch((e) => { if (!cancelled) setPreviewError(messageOf(e)) })
    return () => { cancelled = true }
  }, [api, column.reportColumnId, body, changed, valid])

  const canSave = changed && valid && !!reason.trim() && !busy
  const save = async () => {
    if (!canSave) return
    setBusy(true)
    try {
      const r = await api.columnSettingSave(column.reportColumnId, { ...body, reason: reason.trim() })
      const s = r.summary
      onDone(s ? `${column.displayName}: ${words(s.Visibility_After, s.Groups_After)}. ${s.People_Before.toLocaleString('en-GB')} → ${s.People_After.toLocaleString('en-GB')} ${s.People_After === 1 ? 'user sees' : 'users see'} it.`
        : `${column.displayName} saved.`)
    } catch (e) {
      onError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const choice = (v: 'EVERYONE' | 'HIDDEN' | 'GROUPS', title: string, detail: string) => (
    <button type="button" role="radio" aria-checked={vis === v} onClick={() => setVis(v)}
      className={`flex flex-col items-start gap-0.5 rounded-md border px-3 py-2 text-left transition-colors ${
        vis === v ? 'border-(--color-navy-700) bg-(--color-navy-50) ring-1 ring-(--color-navy-700)' : 'border-(--color-line) bg-(--color-surface) hover:border-(--color-navy-300)'}`}>
      <span className="font-semibold text-(--color-ink)">{title}</span>
      <span className="text-(length:--text-small) text-(--color-ink-3)">{detail}</span>
    </button>
  )

  return (
    <SlideOver
      title={`Who sees ${column.displayName} on ${reportName}`}
      // The report, then the column, each named (owner, 29 Oct 2026).
      heading={
        <span className="flex flex-col gap-0.5">
          <span><span className="font-normal text-(--color-ink-3)">Report:</span> {reportName}</span>
          <span><span className="font-normal text-(--color-ink-3)">Column:</span> {column.displayName}</span>
        </span>
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant={vis === 'HIDDEN' ? 'danger' : 'primary'} onClick={() => void save()} disabled={!canSave}>
            {busy ? 'Saving…' : vis === 'HIDDEN' ? 'Set to No one' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {/* No one, Everyone, Column groups, in that order (owner, 29 Oct 2026). */}
        <div role="radiogroup" aria-label="Who sees it" className="grid grid-cols-1 gap-2">
          {choice('HIDDEN', 'No one', 'Only the system roles see it. Every column starts here.')}
          {choice('EVERYONE', 'Everyone', 'Anyone who can open the report.')}
          {choice('GROUPS', 'Column groups', 'Users who hold any one of the groups ticked below.')}
        </div>

        {vis === 'GROUPS' && (
          <fieldset className="flex flex-col gap-1.5">
            <legend className="eyebrow mb-1.5">{reportName}&apos;s column groups</legend>
            {groups.length === 0 ? (
              <Notice tone="warn" title="This report has no column groups yet.">
                <button type="button" className="font-medium text-(--color-navy-700) hover:underline" onClick={onMakeGroup}>Make one first</button>, then come back to this column.
              </Notice>
            ) : groups.map((g) => {
              const on = picked.includes(g.groupId)
              return (
                <CheckTile key={g.groupId} on={on} changed={on !== initialGroups.includes(g.groupId)}
                  onChange={() => setPicked((p) => (p.includes(g.groupId) ? p.filter((x) => x !== g.groupId) : [...p, g.groupId]))}
                  hint={g.description ?? undefined}>
                  <span className="flex items-center gap-2">
                    <span className="min-w-0 flex-1">{g.groupName}</span>
                    {/* "Employees: N", 0 included (owner, 29 Oct 2026); none still shows red. */}
                    {g.peopleHolding === 0
                      ? <Badge tone="bad">Employees: 0</Badge>
                      : <span className="shrink-0 tabular-nums text-(--color-ink-3)">Employees: {g.peopleHolding.toLocaleString('en-GB')}</span>}
                  </span>
                </CheckTile>
              )
            })}
          </fieldset>
        )}

        {changed && !valid && <Verdict ok={false} title="Tick at least one column group, or choose No one or Everyone." />}
        {changed && valid && previewError && <Verdict ok={false} title="This change would be refused.">{previewError}</Verdict>}
        {changed && valid && preview && (
          preview.People_After === 0 && vis !== 'EVERYONE' ? (
            <Verdict ok={false} title="Only the system roles will see this column.">
              {preview.People_Before.toLocaleString('en-GB')} {preview.People_Before === 1 ? 'user sees' : 'users see'} it now.
            </Verdict>
          ) : (
            <Verdict ok={null} tone={preview.People_After > preview.People_Before ? 'warn' : undefined}
              title={preview.People_After > preview.People_Before ? 'More users will see it'
                : preview.People_After < preview.People_Before ? 'Fewer users will see it' : 'As many users will see it'}>
              {preview.People_Before.toLocaleString('en-GB')} {preview.People_Before === 1 ? 'user sees' : 'users see'} it now;{' '}
              {preview.People_After.toLocaleString('en-GB')} will after this change.
            </Verdict>
          )
        )}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="column-reason" className="eyebrow">Reason Of The Change (required)</label>
          <textarea id="column-reason" rows={3} className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. The managers agreed cost is for the Sales cost group only" />
        </div>
      </div>
    </SlideOver>
  )
}

function words(vis: string, groups: string | null): string {
  return vis === 'EVERYONE' ? 'Everyone' : vis === 'HIDDEN' ? 'No one' : `Column groups ${groups ?? ''}`.trim()
}

/** Make a column group for this report, or rename or describe one. */
function GroupPanel({ api, group, reportSlug, reportName, onClose, onDone }: {
  api: Api
  group: ColumnGroupRow | null
  reportSlug: string
  reportName: string
  onClose: () => void
  onDone: (m: string) => void
}) {
  const [name, setName] = useState(group?.groupName ?? '')
  const [description, setDescription] = useState(group?.description ?? '')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const changed = !group || name.trim() !== group.groupName || (description.trim() || null) !== (group.description ?? null)
  const canSave = changed && !!name.trim() && name.trim().length <= 80 && description.trim().length <= 400 && !!reason.trim() && !busy

  const save = async () => {
    if (!canSave) return
    setBusy(true)
    setError(null)
    try {
      const body = { name: name.trim(), description: description.trim() || null, reason: reason.trim() }
      if (group) {
        await api.columnGroupSave(group.groupId, { ...body, expectedVersion: group.version })
        onDone(`Column group "${body.name}" saved.`)
      } else {
        await api.columnGroupCreate({ ...body, reportSlug })
        onDone(`Column group "${body.name}" made for ${reportName}. Choose it on the columns it should show.`)
      }
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SlideOver
      title={group ? `Edit ${group.groupName}` : `New column group for ${reportName}`}
      description={group ? `Its report, ${reportName}, never changes.` : `It belongs to ${reportName} alone: its columns, and only where ${reportName} is given.`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={() => void save()} disabled={!canSave}>{busy ? 'Saving…' : group ? 'Save' : 'Create group'}</Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Notice tone="bad">{error}</Notice>}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="group-name" className="eyebrow">Name</label>
          <input id="group-name" className={inputClass} maxLength={80} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Sales cost" />
          <span className="text-(length:--text-small) text-(--color-ink-3)">Unique on {reportName}; up to 80 characters.</span>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="group-description" className="eyebrow">Description (optional)</label>
          <textarea id="group-description" rows={2} className={inputClass} maxLength={400} value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Cost, GP and GP%: for managers and Finance" />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="group-reason" className="eyebrow">Reason Of The Change (required)</label>
          <textarea id="group-reason" rows={2} className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
      </div>
    </SlideOver>
  )
}

/** Delete a column group nothing uses; one in use lists where, to take it off those first. */
function DeleteGroupPanel({ api, group, reportName, onClose, onDone }: {
  api: Api
  group: ColumnGroupRow
  reportName: string
  onClose: () => void
  onDone: (m: string) => void
}) {
  const [page, setPage] = useState<ColumnGroupPage | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api.columnGroup(group.groupId)
      .then((p) => { if (!cancelled) setPage(p) })
      .catch((e) => { if (!cancelled) setFailure(e instanceof ApiError ? e.failure : { kind: 'error', title: 'Could not read where it is used.' }) })
    return () => { cancelled = true }
  }, [api, group.groupId])

  const inUse = !!page && page.columns.length + page.roles.length + page.people.length + page.scopes.length > 0
  const remove = async () => {
    if (!page?.group || inUse || !reason.trim()) return
    setBusy(true)
    setError(null)
    try {
      await api.columnGroupDelete(group.groupId, page.group.version, reason.trim())
      onDone(`Column group "${group.groupName}" deleted.`)
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(false)
    }
  }

  const list = (title: string, items: string[]) => items.length > 0 && (
    <div className="flex flex-col gap-1">
      <span className="eyebrow">{title} · {items.length}</span>
      <ul className="list-disc pl-5 text-(length:--text-small) text-(--color-ink-2)">{items.map((x) => <li key={x}>{x}</li>)}</ul>
    </div>
  )

  return (
    <SlideOver
      title={inUse ? `${group.groupName} is in use` : `Delete ${group.groupName}?`}
      description={`A column group of ${reportName}.`}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>{inUse ? 'Close' : 'Cancel'}</Button>
          {!inUse && <Button variant="danger" onClick={() => void remove()} disabled={!page || busy || !reason.trim()}>{busy ? 'Deleting…' : 'Delete'}</Button>}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error && <Notice tone="bad">{error}</Notice>}
        {failure ? <FailureState failure={failure} /> : !page ? <Loading label="Reading where it is used…" /> : inUse ? (
          <>
            <Verdict ok={false} title="It cannot be deleted while it is used.">Take it off each of these first, then delete it.</Verdict>
            {list('Columns', page.columns.map((c) => c.displayName))}
            {list('HR Roles', page.roles.map((r) => [r.roleName, r.roleFunction].filter(Boolean).join(' - ') || `Role ${r.roleId}`))}
            {list('Users’ overrides', page.people.map((p) => `${p.displayName ?? `User ${p.mindzerUserId}`} (${p.effect === 'A' ? 'on' : 'off'})`))}
            {list('Additional scopes', page.scopes.map((s) => `${s.displayName ?? `User ${s.mindzerUserId}`}: ${s.compId}${s.businessUnit ? ` ${s.businessUnit}` : ''}${s.businessTeam ? ` ${s.businessTeam}` : ''}`))}
          </>
        ) : (
          <>
            <Verdict ok={null} title="Nothing uses it.">Deleting it changes nobody&apos;s access.</Verdict>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="group-delete-reason" className="eyebrow">Reason Of The Change (required)</label>
              <textarea id="group-delete-reason" rows={2} className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
          </>
        )}
      </div>
    </SlideOver>
  )
}
